import React, { useState, useEffect, useCallback } from 'react';
import {
  Form, Input, InputNumber, Select, Button, message, Spin,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const { TextArea } = Input;
const { Option }   = Select;
const GOLD = '#B8892A';
const JPY_RATE_FALLBACK = 0.043; // JPY → CNY

const HOUSE_OPTIONS = [
  { value: '', label: '由平台 AI 推荐最优场次' },
  { value: 'ecoauc',    label: 'Eco Auc（腕表专场）' },
  { value: 'jwa',       label: 'JWA（高端专场）' },
  { value: 'timeless',  label: 'Timeless（腕表专场）' },
  { value: 'otakaraya', label: 'おたからや（综合场）' },
  { value: 'daikokuya', label: '大黒屋（综合场）' },
  { value: 'komehyo',   label: 'Komehyo（综合场）' },
  { value: 'naniwa',    label: 'Naniwa（综合场）' },
  { value: 'tokioka',   label: 'トキオカ（综合场）' },
  { value: 'yba',       label: 'YBA（综合场）' },
  { value: 'gta',       label: 'GTA（综合场）' },
];

const STEPS = ['选择商品', 'AI 评估', '确认出品', '寄送日本仓', '上拍结算'];

function StepBar({ current }) {
  return (
    <div style={{
      background: '#fff',
      borderBottom: '1px solid #E8E0D0',
      padding: '12px 24px',
    }}>
      <div style={{ maxWidth: 960, margin: '0 auto', display: 'flex', alignItems: 'center' }}>
        {STEPS.map((label, i) => {
          const isDone   = i < current;
          const isActive = i === current;
          return (
            <React.Fragment key={i}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: isDone ? '#2E7D4F' : isActive ? '#1A1612' : '#EDEAE3',
                  color: isDone || isActive ? '#fff' : '#B0A898',
                  border: isActive || isDone ? 'none' : '1.5px solid #D8D2C8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700, flexShrink: 0,
                }}>
                  {isDone ? '✓' : i + 1}
                </div>
                <span style={{
                  fontSize: 12, fontWeight: 500,
                  color: isActive ? '#1A1612' : isDone ? '#2E7D4F' : '#B0A898',
                }}>
                  {label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div style={{
                  flex: 1, height: 1,
                  background: isDone ? '#2E7D4F' : '#EAE6DF',
                  margin: '0 8px',
                }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

export default function ReverseApply() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [myOrders, setMyOrders]     = useState([]);
  const [houses, setHouses]         = useState([]);
  const [JPY_RATE, setJpyRate] = useState(JPY_RATE_FALLBACK);
  useEffect(() => {
    api.get('/exchange-rates/current').then(({ data }) => {
      const r = data?.rate; if (r && r > 0) setJpyRate(Number(r));
    }).catch(() => {});
  }, []);

  const floorJpy  = Form.useWatch('floor_price_jpy', form) || 0;
  const location  = Form.useWatch('item_location', form) || 'cn';
  const cnyFloor  = Math.round(floorJpy * JPY_RATE);
  const houseFee  = 0.12; // ~12%
  const platFee   = 0.06; // 6%
  const logistic  = 800;
  const estNet    = floorJpy > 0
    ? Math.round(cnyFloor * (1 - houseFee - platFee) - logistic)
    : 0;

  const loadOrders = useCallback(async () => {
    try {
      const res = await api.get('/orders?status=delivered&limit=50');
      setMyOrders(res.data.data || []);
    } catch { /* ignore */ }
  }, []);

  const loadHouses = useCallback(async () => {
    try {
      const res = await api.get('/auction-houses?limit=50');
      setHouses(res.data.data || res.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadOrders(); loadHouses(); }, [loadOrders, loadHouses]);

  const handleSubmit = async () => {
    try {
      const vals = await form.validateFields();
      // resolve preferred_house_id from code to id
      let preferredHouseId = null;
      if (vals.preferred_house_code) {
        const found = houses.find(h => h.code === vals.preferred_house_code);
        if (found) preferredHouseId = found.id;
      }
      setSubmitting(true);
      const res = await api.post('/consignments/reverse', {
        source_order_id:   vals.source_order_id || null,
        item_name:         vals.item_name,
        item_description:  vals.item_description || '',
        item_grade:        vals.item_grade || '',
        floor_price_jpy:   vals.floor_price_jpy,
        preferred_house_id: preferredHouseId,
        item_location:     vals.item_location,
        pickup_address:    vals.pickup_address || '',
        notes:             vals.notes || '',
      });
      message.success('申请已提交！AI 将在 24 小时内完成评估');
      navigate('/reverse-tracking');
    } catch (err) {
      if (!err?.errorFields) message.error(err?.response?.data?.error || '提交失败');
    } finally { setSubmitting(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#FAF8F5' }}>
      <StepBar current={0} />

      <div style={{
        maxWidth: 960, margin: '0 auto', padding: '28px 24px',
        display: 'grid', gridTemplateColumns: '1fr 300px', gap: 24,
      }}>
        {/* ── Left ── */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <Button
              icon={<ArrowLeftOutlined />}
              style={{ border: '1px solid #D8D2C8', color: '#7A7268' }}
              onClick={() => navigate('/consignment')}
            >
              返回
            </Button>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, fontFamily: 'var(--serif)' }}>
              反向出品申请
            </h2>
          </div>

          <Form
            form={form}
            layout="vertical"
            initialValues={{ item_location: 'cn', preferred_house_code: '' }}
          >
            {/* Source order */}
            {myOrders.length > 0 && (
              <Form.Item label="关联代购订单（可选）" name="source_order_id">
                <Select placeholder="选择来自代购的商品（可不选）" allowClear>
                  {myOrders.map(o => (
                    <Option key={o.id} value={o.id}>
                      {o.order_no} — {o.title_zh || '—'}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            <Form.Item
              label="商品名称"
              name="item_name"
              rules={[{ required: true, message: '请输入商品名称' }]}
            >
              <Input placeholder="例：Rolex Daytona 116500LN 黑盘 附盒单" />
            </Form.Item>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Form.Item label="品相等级" name="item_grade">
                <Select placeholder="选择品相">
                  <Option value="S">S 级（全新/接近全新）</Option>
                  <Option value="A">A 级（轻微使用）</Option>
                  <Option value="B">B 级（明显使用痕迹）</Option>
                </Select>
              </Form.Item>
              <Form.Item label="物品当前位置" name="item_location">
                <Select>
                  <Option value="cn">在中国（需寄送日本仓）</Option>
                  <Option value="jp">已在日本（平台代办入库）</Option>
                  <Option value="store">存放在平台日本仓</Option>
                </Select>
              </Form.Item>
            </div>

            {location === 'cn' && (
              <Form.Item label="取件地址（平台安排上门取件）" name="pickup_address">
                <Input placeholder="省 / 市 / 区 / 详细地址" />
              </Form.Item>
            )}

            <Form.Item
              label="底价设定（日元）"
              name="floor_price_jpy"
              rules={[{ required: true, message: '请设置底价' }]}
              extra="底价 = 不低于此价格成交。AI 将基于近期成交数据给出建议底价。"
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0} step={10000}
                formatter={v => `¥ ${v} JPY`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={v => v.replace(/¥\s?JPY|(,*)/g, '').trim()}
              />
            </Form.Item>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Form.Item label="希望上拍的拍卖行" name="preferred_house_code">
                <Select>
                  {HOUSE_OPTIONS.map(o => (
                    <Option key={o.value} value={o.value}>{o.label}</Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label="商品描述（选填）" name="item_description">
                <Input placeholder="附件情况、使用背景等" />
              </Form.Item>
            </div>

            <Form.Item label="补充说明（选填）" name="notes">
              <TextArea rows={3} placeholder="购入背景、是否有细微使用痕迹等，有助于拍卖行撰写说明书" />
            </Form.Item>
          </Form>
        </div>

        {/* ── Right: AI Estimate ── */}
        <div>
          <div style={{
            background: '#fff',
            border: '1px solid #E8E0D0',
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 2px 12px rgba(26,22,18,.08)',
            position: 'sticky',
            top: 80,
          }}>
            <div style={{
              padding: '13px 16px',
              background: '#1A1612',
              color: '#fff',
              fontSize: 13, fontWeight: 600,
            }}>
              ✦ AI 出品评估
            </div>
            <div style={{ padding: 16 }}>
              <div style={{
                background: 'rgba(200,146,42,.06)',
                border: '1px solid rgba(200,146,42,.2)',
                borderRadius: 3, padding: 12, marginBottom: 14,
              }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: GOLD, letterSpacing: 1, marginBottom: 6 }}>
                  日本市场预估（提交后生成）
                </div>
                <div style={{ fontSize: 13, color: '#7A7268' }}>
                  AI 将在提交后 24 小时内完成评估，给出详细的成交区间和拍卖行推荐。
                </div>
              </div>

              {[
                ['您设定底价', floorJpy > 0 ? `¥${floorJpy.toLocaleString()} JPY` : '—'],
                ['折合人民币', floorJpy > 0 ? `≈ ¥${cnyFloor.toLocaleString()} CNY` : '—'],
                ['日本拍卖行佣金', '约 12%'],
                ['平台服务费', '6%'],
                ['手续 + 运费', '约 ¥800 CNY'],
              ].map(([k, v]) => (
                <div key={k} style={{
                  display: 'flex', justifyContent: 'space-between',
                  fontSize: 13, paddingBottom: 9, marginBottom: 9,
                  borderBottom: '1px solid #F0EDE8',
                }}>
                  <span style={{ color: '#7A7268' }}>{k}</span>
                  <span style={{ fontFamily: 'var(--mono)' }}>{v}</span>
                </div>
              ))}

              <div style={{
                display: 'flex', justifyContent: 'space-between',
                fontWeight: 600, fontSize: 14, marginTop: 4,
              }}>
                <span style={{ color: '#7A7268' }}>预计到手（底价成交）</span>
                <span style={{ fontFamily: 'var(--mono)', color: GOLD, fontSize: 16 }}>
                  {floorJpy > 0 ? `≈ ¥${estNet.toLocaleString()} CNY` : '—'}
                </span>
              </div>

              <Button
                type="primary"
                block size="large"
                loading={submitting}
                style={{ marginTop: 16, background: '#2E7D4F', borderColor: '#2E7D4F', fontSize: 15 }}
                onClick={handleSubmit}
              >
                提交申请 · 进入 AI 评估 →
              </Button>
            </div>
          </div>

          {/* Flow */}
          <div style={{
            background: '#fff', border: '1px solid #E8E0D0',
            borderRadius: 4, padding: 16, marginTop: 14,
          }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12 }}>反向出品流程</div>
            {[
              ['提交申请', '平台 AI 24小时内完成行情评估'],
              ['安排取件', '上门取件 → 寄往云川仓库'],
              ['日本拍前检品', '重新检品 · 报告翻译为日语'],
              ['正式上拍', '自动化引擎实时监控竞价进展'],
              ['JPY 结算到账', '成交后扣佣 · CNY T+3到账'],
            ].map(([t, s], i) => (
              <div key={i} style={{ display: 'flex', gap: 10, paddingBottom: i < 4 ? 12 : 0, position: 'relative' }}>
                {i < 4 && <div style={{ position: 'absolute', left: 11, top: 24, bottom: 0, width: 2, background: '#EAE6DF' }} />}
                <div style={{
                  width: 24, height: 24, borderRadius: '50%',
                  background: 'rgba(46,125,79,.1)', border: '1.5px solid rgba(46,125,79,.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700, color: '#2E7D4F', flexShrink: 0,
                }}>
                  {i + 1}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{t}</div>
                  <div style={{ fontSize: 11, color: '#7A7268', lineHeight: 1.5 }}>{s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
